import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { NAV_LINKS, SITE, ASSETS } from "@/config/site";
import { cn } from "@/lib/utils";

const Logo = ({ onClick }) => (
  <Link to="/" onClick={onClick} data-testid="nav-logo" className="flex items-center gap-2.5" aria-label="Nova home">
    <img src={ASSETS.icon} alt="Nova app icon" className="h-9 w-9 rounded-xl" width="36" height="36" />
    <span className="font-heading text-lg font-800 tracking-tight text-white" style={{ fontWeight: 800 }}>
      {SITE.name}
    </span>
  </Link>
);

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const handleAnchor = (e, href) => {
    if (href.startsWith("/#")) {
      const id = href.slice(2);
      if (location.pathname === "/") {
        e.preventDefault();
        document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
      } else {
        e.preventDefault();
        navigate("/", { state: { scrollTo: id } });
      }
    }
    setOpen(false);
  };

  return (
    <header
      className={cn(
        "fixed inset-x-0 top-0 z-50 transition-colors duration-500",
        scrolled ? "glass-strong border-b border-white/10" : "border-b border-transparent"
      )}
      data-testid="site-navbar"
    >
      <nav className="container-nova flex h-[68px] items-center justify-between">
        <Logo />

        <div className="hidden items-center gap-0.5 lg:flex">
          {NAV_LINKS.map((l) => (
            <a
              key={l.label}
              href={l.href}
              onClick={(e) => handleAnchor(e, l.href)}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/[^a-z]+/g, "-").replace(/-+$/, "")}`}
              className="group relative rounded-full px-3 py-2 text-sm font-medium text-white/70 transition-colors hover:text-white"
            >
              {l.label}
              <span className="absolute inset-x-3 -bottom-0.5 h-px origin-left scale-x-0 bg-gradient-to-r from-nova-cyan to-nova-emerald transition-transform duration-300 group-hover:scale-x-100" />
            </a>
          ))}
        </div>

        <div className="hidden items-center gap-3 xl:flex">
          <span
            data-testid="nav-appstore-badge"
            className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-2 text-xs font-semibold text-white/80"
          >
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-nova-emerald" />
            Coming soon to the App Store
          </span>
        </div>

        <button
          type="button"
          className="rounded-full border border-white/15 bg-white/5 p-2.5 text-white lg:hidden"
          onClick={() => setOpen((v) => !v)}
          aria-label={open ? "Close menu" : "Open menu"}
          aria-expanded={open}
          data-testid="nav-mobile-toggle"
        >
          {open ? <X size={20} /> : <Menu size={20} />}
        </button>
      </nav>

      {open && (
        <div className="glass-strong border-t border-white/10 lg:hidden" data-testid="nav-mobile-menu">
          <div className="container-nova flex flex-col gap-1 py-5">
            {NAV_LINKS.map((l) => (
              <a
                key={l.label}
                href={l.href}
                onClick={(e) => handleAnchor(e, l.href)}
                className="rounded-xl px-3 py-3 text-base font-medium text-white/80 hover:bg-white/5 hover:text-white"
                data-testid={`nav-mobile-link-${l.label.toLowerCase().replace(/[^a-z]+/g, "-").replace(/-+$/, "")}`}
              >
                {l.label}
              </a>
            ))}
            <span className="mt-3 inline-flex w-fit items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-2 text-xs font-semibold text-white/80">
              <span className="h-1.5 w-1.5 rounded-full bg-nova-emerald" />
              Coming soon to the App Store
            </span>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
